<script setup>
import { ref, computed, watch } from 'vue'
import AppLayout from '@/layouts/AppLayout.vue'

const props = defineProps({
  estimations: { type: Array,   default: () => [] },
})

const openDropdown = ref(null)
function toggleDropdown(id) {
  openDropdown.value = openDropdown.value === id ? null : id
}

// ── Search + sort + pagination ───────────────────────────────
const search      = ref('')
const pageSize    = ref(25)
const currentPage = ref(1)
const sortKey     = ref('code')
const sortDir     = ref('asc')

watch([search, pageSize, sortKey, sortDir], () => { currentPage.value = 1 })

function toggleSort(key) {
  if (sortKey.value === key) {
    sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortKey.value = key
    sortDir.value = 'asc'
  }
}

const filtered = computed(() => {
  let list = props.estimations
  if (search.value.trim()) {
    const q = search.value.toLowerCase()
    list = list.filter(e =>
      e.code.toLowerCase().includes(q) ||
      e.nom.toLowerCase().includes(q) ||
      e.projet_code.toLowerCase().includes(q) ||
      e.description.toLowerCase().includes(q)
    )
  }
  return [...list].sort((a, b) => {
    let aVal = a[sortKey.value] ?? ''
    let bVal = b[sortKey.value] ?? ''
    if (typeof aVal === 'string') aVal = aVal.toLowerCase()
    if (typeof bVal === 'string') bVal = bVal.toLowerCase()
    if (aVal < bVal) return sortDir.value === 'asc' ? -1 : 1
    if (aVal > bVal) return sortDir.value === 'asc' ? 1 : -1
    return 0
  })
})

const totalPages = computed(() => Math.max(1, Math.ceil(filtered.value.length / pageSize.value)))
const paginated  = computed(() => {
  const start = (currentPage.value - 1) * pageSize.value
  return filtered.value.slice(start, start + pageSize.value)
})
const pageNumbers = computed(() => {
  const total = totalPages.value
  const cur   = currentPage.value
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)
  const pages = new Set([1, total, cur, cur - 1, cur + 1].filter(p => p >= 1 && p <= total))
  return [...pages].sort((a, b) => a - b).reduce((acc, p, i, arr) => {
    if (i > 0 && p - arr[i - 1] > 1) acc.push('…')
    acc.push(p)
    return acc
  }, [])
})

function sortIcon(key) {
  if (sortKey.value !== key) return 'unfold_more'
  return sortDir.value === 'asc' ? 'arrow_upward' : 'arrow_downward'
}

const thCls = 'px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline cursor-pointer select-none hover:text-on-surface transition-colors'
const tdCls = 'px-4 py-3 text-sm text-on-surface'
</script>

<template>
  <AppLayout>
    <template #topbar-left>
      <span class="text-sm font-black uppercase tracking-widest text-on-surface">Estimations</span>
    </template>


    <div class="p-8 space-y-6" @click="openDropdown = null">

      <!-- KPI -->
      <div class="grid grid-cols-1 gap-4 max-w-xs">
        <div class="bg-surface-container rounded-xl border border-outline-variant/10 shadow p-5">
          <p class="text-[10px] font-black uppercase tracking-widest text-outline mb-1">Total</p>
          <p class="text-3xl font-black text-on-surface">{{ estimations.length }}</p>
        </div>
      </div>

      <!-- Tableau -->
      <div class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">

        <!-- Header -->
        <div class="px-6 py-4 border-b border-outline-variant/10 bg-surface-container-high/40 flex items-center justify-between gap-4 flex-wrap">
          <h2 class="text-[11px] font-black uppercase tracking-widest text-outline">Liste des estimations</h2>
          <div class="flex items-center gap-3">
            <div class="relative">
              <span class="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-outline text-sm">search</span>
              <input v-model="search" type="text" placeholder="Rechercher…"
                     class="pl-9 pr-4 py-2 text-xs bg-surface-container-highest border border-outline-variant/20 rounded-md text-on-surface placeholder:text-outline focus:outline-none focus:ring-2 focus:ring-primary/20 w-52" />
            </div>
            <select v-model="pageSize"
                    class="text-xs bg-surface-container-highest border border-outline-variant/20 rounded-md px-2 py-2 text-on-surface focus:outline-none">
              <option :value="10">10</option>
              <option :value="25">25</option>
              <option :value="50">50</option>
              <option :value="100">100</option>
            </select>
          </div>
        </div>

        <!-- Table -->
        <div class="overflow-x-auto">
          <table class="w-full text-sm border-collapse">
            <thead>
              <tr class="bg-surface-container-high/30 border-b border-outline-variant/10">
                <th :class="thCls" @click="toggleSort('projet_code')">
                  Projet <span class="material-symbols-outlined text-xs align-middle">{{ sortIcon('projet_code') }}</span>
                </th>
                <th :class="thCls" @click="toggleSort('code')">
                  Code <span class="material-symbols-outlined text-xs align-middle">{{ sortIcon('code') }}</span>
                </th>
                <th :class="thCls" @click="toggleSort('nom')">
                  Nom <span class="material-symbols-outlined text-xs align-middle">{{ sortIcon('nom') }}</span>
                </th>
                <th :class="thCls" @click="toggleSort('date_soumission')">
                  Soumission <span class="material-symbols-outlined text-xs align-middle">{{ sortIcon('date_soumission') }}</span>
                </th>
                <th :class="thCls" @click="toggleSort('date_acceptation')">
                  Acceptation <span class="material-symbols-outlined text-xs align-middle">{{ sortIcon('date_acceptation') }}</span>
                </th>
                <th :class="thCls">Description</th>
                <th class="px-4 py-3 text-[10px] font-black uppercase tracking-widest text-outline text-right w-20">Actions</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-outline-variant/10">
              <tr v-if="paginated.length === 0">
                <td colspan="7" class="px-4 py-12 text-center text-outline text-sm">Aucune estimation trouvée</td>
              </tr>
              <template v-for="e in paginated" :key="e.id">
                <tr class="hover:bg-surface-container-highest/20 transition-colors">
                  <td :class="tdCls + ' whitespace-nowrap'">
                    <span class="font-mono font-bold text-primary text-xs">{{ e.projet_code }}</span>
                  </td>
                  <td :class="tdCls + ' whitespace-nowrap'">
                    <a :href="e.show_url" class="font-mono font-bold text-primary hover:brightness-125 transition-colors">{{ e.code }}</a>
                  </td>
                  <td :class="tdCls + ' font-medium'">{{ e.nom }}</td>
                  <td :class="tdCls + ' font-mono text-outline text-xs whitespace-nowrap'">{{ e.date_soumission ?? '—' }}</td>
                  <td :class="tdCls + ' font-mono text-outline text-xs whitespace-nowrap'">{{ e.date_acceptation ?? '—' }}</td>
                  <td :class="tdCls + ' text-outline/70 text-xs max-w-xs truncate'">{{ e.description }}</td>
                  <td class="px-4 py-3 text-right">
                    <div class="relative inline-block">
                      <button @click.stop="toggleDropdown(e.id)"
                              class="inline-flex p-2 text-outline hover:text-primary hover:bg-primary/10 rounded transition-all">
                        <span class="material-symbols-outlined text-lg">edit_note</span>
                      </button>
                      <div v-if="openDropdown === e.id"
                           class="absolute right-0 top-full z-50 mt-1 bg-surface-container-high border border-outline-variant/20 rounded-lg shadow-xl min-w-[160px] py-1">
                        <a :href="e.show_url" class="flex items-center gap-2 px-4 py-2 text-sm text-outline hover:text-on-surface hover:bg-surface-container-highest transition-colors">
                          <span class="material-symbols-outlined text-base">visibility</span>Voir
                        </a>
                        <a :href="e.edit_url" class="flex items-center gap-2 px-4 py-2 text-sm text-outline hover:text-on-surface hover:bg-surface-container-highest transition-colors">
                          <span class="material-symbols-outlined text-base">edit</span>Modifier
                        </a>

                      </div>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div v-if="totalPages > 1" class="px-6 py-4 border-t border-outline-variant/10 flex items-center justify-between gap-4">
          <span class="text-[10px] font-black uppercase tracking-widest text-outline">
            Page {{ currentPage }} / {{ totalPages }}
          </span>
          <div class="flex items-center gap-1">
            <button @click="currentPage = Math.max(1, currentPage - 1)" :disabled="currentPage === 1"
                    class="px-2 py-1.5 text-xs text-outline hover:text-on-surface border border-outline-variant/20 rounded disabled:opacity-30 transition-all">
              <span class="material-symbols-outlined text-sm">chevron_left</span>
            </button>
            <template v-for="p in pageNumbers" :key="p">
              <span v-if="p === '…'" class="px-2 text-outline text-xs">…</span>
              <button v-else @click="currentPage = p"
                      :class="['px-3 py-1.5 text-xs rounded border transition-all', p === currentPage ? 'bg-primary text-on-primary border-primary font-black' : 'text-outline hover:text-on-surface border-outline-variant/20']">
                {{ p }}
              </button>
            </template>
            <button @click="currentPage = Math.min(totalPages, currentPage + 1)" :disabled="currentPage === totalPages"
                    class="px-2 py-1.5 text-xs text-outline hover:text-on-surface border border-outline-variant/20 rounded disabled:opacity-30 transition-all">
              <span class="material-symbols-outlined text-sm">chevron_right</span>
            </button>
          </div>
        </div>

      </div>
    </div>
  </AppLayout>
</template>
